/**
 * useTeams — client wrapper for /api/teams + /api/audit/me.
 */
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getBackendBaseUrl } from '@/lib/backendUrl';

const BACKEND_URL = getBackendBaseUrl();
const ROOT = `${BACKEND_URL}/api/teams`;

export const useTeams = () => {
  const { session } = useAuth();
  const [teams, setTeams] = useState([]);
  const [activeTeam, setActiveTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const headers = useCallback((extra = {}) => {
    const h = { 'Content-Type': 'application/json', ...extra };
    if (session?.access_token) h.Authorization = `Bearer ${session.access_token}`;
    return h;
  }, [session]);

  const _safe = useCallback(async (fn) => {
    setError(null);
    try { return await fn(); }
    catch (e) { setError(e?.message || 'Network error'); throw e; }
  }, []);

  const listTeams = useCallback(async () => {
    setIsLoading(true);
    try {
      return await _safe(async () => {
        const res = await fetch(`${ROOT}`, { headers: headers() });
        if (!res.ok) throw new Error(`teams ${res.status}`);
        const data = await res.json();
        setTeams(data.teams || []);
        return data.teams || [];
      });
    } finally { setIsLoading(false); }
  }, [_safe, headers]);

  const createTeam = useCallback(async ({ name, name_ar = '' }) => {
    return _safe(async () => {
      const res = await fetch(`${ROOT}`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ name, name_ar }),
      });
      if (!res.ok) throw new Error(`create ${res.status}`);
      const data = await res.json();
      return data.team;
    });
  }, [_safe, headers]);

  const getTeam = useCallback(async (id) => {
    return _safe(async () => {
      const res = await fetch(`${ROOT}/${encodeURIComponent(id)}`, { headers: headers() });
      if (!res.ok) throw new Error(`get ${res.status}`);
      const data = await res.json();
      setActiveTeam({ ...data.team, my_role: data.my_role });
      setMembers(data.members || []);
      return data;
    });
  }, [_safe, headers]);

  const inviteMember = useCallback(async (teamId, { email, role = 'member', language = 'en' }) => {
    return _safe(async () => {
      const res = await fetch(`${ROOT}/${encodeURIComponent(teamId)}/invite`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ email, role, language }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `invite ${res.status}`);
      }
      return await res.json();   // {invite, invite_link, email}
    });
  }, [_safe, headers]);

  const acceptInvite = useCallback(async (token) => {
    return _safe(async () => {
      const res = await fetch(`${ROOT}/accept-invite`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ token }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `accept ${res.status}`);
      }
      return await res.json();
    });
  }, [_safe, headers]);

  const removeMember = useCallback(async (teamId, userId) => {
    return _safe(async () => {
      const res = await fetch(`${ROOT}/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`, {
        method: 'DELETE', headers: headers(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `remove ${res.status}`);
      }
      return true;
    });
  }, [_safe, headers]);

  const updateRole = useCallback(async (teamId, userId, role) => {
    return _safe(async () => {
      const res = await fetch(`${ROOT}/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`, {
        method: 'PATCH', headers: headers(),
        body: JSON.stringify({ role }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `update ${res.status}`);
      }
      const data = await res.json();
      return data.member;
    });
  }, [_safe, headers]);

  const fetchTeamAudit = useCallback(async (teamId) => {
    return _safe(async () => {
      const res = await fetch(`${ROOT}/${encodeURIComponent(teamId)}/audit-logs`, { headers: headers() });
      if (!res.ok) throw new Error(`audit ${res.status}`);
      const data = await res.json();
      setAuditLogs(data.logs || []);
      return data.logs || [];
    });
  }, [_safe, headers]);

  const fetchMyAudit = useCallback(async () => {
    return _safe(async () => {
      const res = await fetch(`${BACKEND_URL}/api/audit/me`, { headers: headers() });
      if (!res.ok) throw new Error(`audit/me ${res.status}`);
      const data = await res.json();
      setAuditLogs(data.logs || []);
      return data.logs || [];
    });
  }, [_safe, headers]);

  useEffect(() => {
    if (!session?.access_token) return;
    listTeams().catch(() => {});
  // Cluster G.C (Feb 2026): omitting `listTeams` is intentional. It
  // is a useCallback that rebuilds on every render of the parent
  // header; including it would cause an infinite re-fetch loop. The
  // deliberate dep `session?.access_token` gives us one fetch per
  // auth state change, which is the intended behaviour.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.access_token]);

  return {
    teams, activeTeam, members, auditLogs, isLoading, error,
    listTeams, createTeam, getTeam, inviteMember, acceptInvite,
    removeMember, updateRole, fetchTeamAudit, fetchMyAudit,
  };
};

export default useTeams;
